/**
 * SampleDataGenerator - Admin component for generating test data
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import {
  Loader2, Wand2, Users, Calendar, CreditCard, Trash2, AlertCircle,
  CheckCircle2, RefreshCw, DatabaseZap
} from 'lucide-react';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SampleDataGenerator = ({ eventId }) => {
  const [loading, setLoading] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [lastGeneration, setLastGeneration] = useState(null);

  const [config, setConfig] = useState({
    delegates: 50,
    sessions: 10,
    payments: true,
    checkins: 30,
  });

  const token = getToken();

  const fetchStats = async () => {
    setLoadingStats(true);
    try {
      const response = await fetch(`${API_URL}/api/events/${eventId}/sample-data/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    } finally {
      setLoadingStats(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [eventId]);

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/events/${eventId}/sample-data/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(config)
      });

      const data = await response.json();

      if (response.ok) {
        setLastGeneration(data.results);
        toast.success('Sample data generated successfully!');
        fetchStats();
      } else {
        throw new Error(data.detail || 'Failed to generate sample data');
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = async () => {
    if (!confirm('Are you sure you want to clear all sample data? This cannot be undone.')) {
      return;
    }

    setClearing(true);
    try {
      const response = await fetch(`${API_URL}/api/events/${eventId}/sample-data/clear`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          delegates: true,
          sessions: true,
          invitations: true
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Sample data cleared successfully!');
        setLastGeneration(null);
        fetchStats();
      } else {
        throw new Error(data.detail || 'Failed to clear sample data');
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <DatabaseZap className="h-6 w-6 text-indigo-600" />
            Sample Data Generator
          </h2>
          <p className="text-muted-foreground">
            Generate realistic test data for your event
          </p>
        </div>
        <Button onClick={fetchStats} variant="outline" disabled={loadingStats}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loadingStats ? 'animate-spin' : ''}`} />
          Refresh Stats
        </Button>
      </div>

      {/* Current Data Stats */}
      <Card>
        <CardHeader>
          <CardTitle>Current Data</CardTitle>
          <CardDescription>Overview of existing data in this event</CardDescription>
        </CardHeader>
        <CardContent>
          {loadingStats ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="text-center p-4 bg-slate-50 rounded-lg">
                <Users className="h-6 w-6 mx-auto text-blue-600 mb-2" />
                <div className="text-2xl font-bold">{stats?.delegates || 0}</div>
                <div className="text-sm text-muted-foreground">Delegates</div>
              </div>
              <div className="text-center p-4 bg-slate-50 rounded-lg">
                <Calendar className="h-6 w-6 mx-auto text-green-600 mb-2" />
                <div className="text-2xl font-bold">{stats?.sessions || 0}</div>
                <div className="text-sm text-muted-foreground">Sessions</div>
              </div>
              <div className="text-center p-4 bg-slate-50 rounded-lg">
                <CreditCard className="h-6 w-6 mx-auto text-purple-600 mb-2" />
                <div className="text-2xl font-bold">{stats?.payments || 0}</div>
                <div className="text-sm text-muted-foreground">Payments</div>
              </div>
              <div className="text-center p-4 bg-slate-50 rounded-lg">
                <div className="h-6 w-6 mx-auto text-orange-600 mb-2">🎫</div>
                <div className="text-2xl font-bold">{stats?.tiers || 0}</div>
                <div className="text-sm text-muted-foreground">Ticket Tiers</div>
              </div>
              <div className="text-center p-4 bg-slate-50 rounded-lg">
                <div className="h-6 w-6 mx-auto text-pink-600 mb-2">✉️</div>
                <div className="text-2xl font-bold">{stats?.invitations || 0}</div>
                <div className="text-sm text-muted-foreground">Invitations</div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Generator Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wand2 className="h-5 w-5" />
            Generate Sample Data
          </CardTitle>
          <CardDescription>
            Configure what test data to generate for this event
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Delegates */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <Label className="text-base">Delegates</Label>
              <Badge variant="outline">{config.delegates}</Badge>
            </div>
            <Slider
              value={[config.delegates]}
              onValueChange={([value]) => setConfig(prev => ({ ...prev, delegates: value }))}
              max={500}
              min={0}
              step={10}
            />
            <p className="text-sm text-muted-foreground">
              Generate {config.delegates} realistic delegate profiles with random names, companies, and titles
            </p>
          </div>

          <Separator />

          {/* Sessions */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <Label className="text-base">Sessions</Label>
              <Badge variant="outline">{config.sessions}</Badge>
            </div>
            <Slider
              value={[config.sessions]}
              onValueChange={([value]) => setConfig(prev => ({ ...prev, sessions: value }))}
              max={50}
              min={0}
              step={1}
            />
            <p className="text-sm text-muted-foreground">
              Create {config.sessions} sessions including keynotes, workshops, and panels
            </p>
          </div>

          <Separator />

          {/* Check-in Percentage */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <Label className="text-base">Check-in Rate</Label>
              <Badge variant="outline">{config.checkins}%</Badge>
            </div>
            <Slider
              value={[config.checkins]}
              onValueChange={([value]) => setConfig(prev => ({ ...prev, checkins: value }))}
              max={100}
              min={0}
              step={5}
            />
            <p className="text-sm text-muted-foreground">
              {config.checkins}% of delegates will be marked as checked in
            </p>
          </div>

          <Separator />

          {/* Generate Payments */}
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base">Generate Payments</Label>
              <p className="text-sm text-muted-foreground">
                Create payment records for confirmed delegates
              </p>
            </div>
            <Switch
              checked={config.payments}
              onCheckedChange={(checked) => setConfig(prev => ({ ...prev, payments: checked }))}
            />
          </div>
        </CardContent>
        <CardFooter className="flex justify-between">
          <Button variant="outline" onClick={() => setConfig({ delegates: 50, sessions: 10, payments: true, checkins: 30 })}>
            Reset to Defaults
          </Button>
          <Button onClick={handleGenerate} disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Wand2 className="h-4 w-4 mr-2" />
                Generate Data
              </>
            )}
          </Button>
        </CardFooter>
      </Card>

      {/* Last Generation Results */}
      {lastGeneration && (
        <Card className="border-green-200 bg-green-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-800">
              <CheckCircle2 className="h-5 w-5" />
              Generation Complete
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {lastGeneration.generated?.delegates && (
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-700">
                    +{lastGeneration.generated.delegates}
                  </div>
                  <div className="text-sm text-green-600">Delegates</div>
                </div>
              )}
              {lastGeneration.generated?.sessions && (
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-700">
                    +{lastGeneration.generated.sessions}
                  </div>
                  <div className="text-sm text-green-600">Sessions</div>
                </div>
              )}
              {lastGeneration.generated?.payments && (
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-700">
                    +{lastGeneration.generated.payments}
                  </div>
                  <div className="text-sm text-green-600">Payments</div>
                </div>
              )}
              {lastGeneration.generated?.tiers && (
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-700">
                    +{lastGeneration.generated.tiers}
                  </div>
                  <div className="text-sm text-green-600">Tiers</div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Clear Data */}
      <Card className="border-red-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-700">
            <Trash2 className="h-5 w-5" />
            Clear Sample Data
          </CardTitle>
          <CardDescription>
            Remove all delegates, sessions, payments, and invitations from this event
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Warning</AlertTitle>
            <AlertDescription>
              This action cannot be undone. All event data will be permanently deleted.
            </AlertDescription>
          </Alert>
        </CardContent>
        <CardFooter>
          <Button 
            variant="destructive" 
            onClick={handleClear}
            disabled={clearing}
          >
            {clearing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Clearing...
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4 mr-2" />
                Clear All Data
              </>
            )}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default SampleDataGenerator;
